import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSimulatedRole } from '@/hooks/useSimulatedRole';

export type UserRole = 'god_mode' | 'admin' | 'sovereign' | 'demi_user' | 'guest';

// Cache keys for instant nav loading
const USER_ROLE_CACHE_KEY = 'aether_user_role';
const USER_ID_CACHE_KEY = 'aether_cached_user_id';

// Get cached role for this specific user
const getCachedRole = (userId: string): UserRole | null => {
  try {
    const cachedUserId = localStorage.getItem(USER_ID_CACHE_KEY);
    if (cachedUserId !== userId) return null;
    
    const cached = localStorage.getItem(USER_ROLE_CACHE_KEY);
    if (cached && ['god_mode', 'admin', 'sovereign', 'demi_user', 'guest'].includes(cached)) {
      return cached as UserRole;
    }
  } catch {
    // localStorage not available
  }
  return null;
};

// Save role to cache
const cacheRole = (userId: string, role: UserRole) => {
  try {
    localStorage.setItem(USER_ID_CACHE_KEY, userId);
    localStorage.setItem(USER_ROLE_CACHE_KEY, role);
  } catch {
    // localStorage not available
  }
};

// Clear cache (exported for use in AuthContext)
export const clearRoleCache = () => {
  try {
    localStorage.removeItem(USER_ROLE_CACHE_KEY);
    localStorage.removeItem(USER_ID_CACHE_KEY);
  } catch {
    // localStorage not available
  }
};

interface Community {
  id: string;
  name: string;
  role: string;
  property_id?: string;
  logo_url?: string | null;
}

interface OwnedProperty {
  id: string;
  address: string;
  community_id: string;
  is_rental_mode: boolean;
  bedrooms?: number | null;
  bathrooms?: number | null;
  sqft?: number | null;
}

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  avatar_url: string | null;
}

interface UserContext {
  user_id: string;
  profile: Profile | null;
  primary_role: UserRole;
  global_roles: string[];
  is_god_mode: boolean;
  communities: Community[];
  owned_properties: OwnedProperty[];
}

export const useUserContext = () => {
  const { user, session } = useAuth();
  const { simulatedRole, isSimulating } = useSimulatedRole();
  const [userContext, setUserContext] = useState<UserContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get cached role immediately for this user
  const cachedRole = user?.id ? getCachedRole(user.id) : null;

  const fetchUserContext = useCallback(async () => {
    if (!session?.access_token) {
      setUserContext(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fnError } = await supabase.functions.invoke('get-user-context', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (fnError) throw fnError;
      
      const ctx = data as UserContext;
      setUserContext(ctx);
      
      // Cache the role for instant nav on next load
      if (user?.id && ctx.primary_role) {
        cacheRole(user.id, ctx.primary_role);
      }
    } catch (err) {
      console.error('Error fetching user context:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch user context');
      setUserContext(null);
    } finally {
      setIsLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    if (user && session) {
      fetchUserContext();
    } else {
      setUserContext(null);
      setIsLoading(false);
    }
  }, [user, session, fetchUserContext]);

  // Compute effective role - simulated role overrides actual role
  // Use cached role while loading for instant nav
  const effectiveRole: UserRole = isSimulating && simulatedRole 
    ? simulatedRole 
    : (userContext?.primary_role || cachedRole || 'guest');

  // Use cached role to compute boolean flags when context is loading
  const roleToCheck = userContext?.primary_role || cachedRole;
  
  const effectiveIsGodMode = isSimulating 
    ? simulatedRole === 'god_mode' 
    : (userContext?.is_god_mode || roleToCheck === 'god_mode' || false);

  const effectiveIsAdmin = isSimulating 
    ? simulatedRole === 'admin' || simulatedRole === 'god_mode'
    : (roleToCheck === 'admin' || userContext?.is_god_mode || roleToCheck === 'god_mode' || false);

  const effectiveIsSovereign = isSimulating 
    ? simulatedRole === 'sovereign' 
    : (roleToCheck === 'sovereign' || false);

  const effectiveIsDemiUser = isSimulating 
    ? simulatedRole === 'demi_user' 
    : (roleToCheck === 'demi_user' || false);

  return {
    userContext,
    isLoading,
    error,
    refetch: fetchUserContext,
    primaryRole: effectiveRole,
    isGodMode: effectiveIsGodMode,
    isAdmin: effectiveIsAdmin,
    isSovereign: effectiveIsSovereign,
    isDemiUser: effectiveIsDemiUser,
    isSimulating,
  };
};
